/**
 * The cohort report's view-model, shared by the operator, brand and affiliate
 * consoles (Rule Book 35.3a).
 *
 * The API answers one row per cohort, checkpoint, group and currency. This
 * turns that into the grid on screen: one row per cohort (and group), one
 * column per checkpoint, the chosen metric in each cell, formatted here
 * because money is formatted in the view-model layer and never in a component.
 *
 * Counts add across currencies (every player reports in exactly one); money
 * never does, so a money metric shows one currency at a time.
 */
import { DISPLAY_CURRENCY_ORIGINAL } from './currencies.ts';
import { kitText, type KitMessageKey } from './messages.ts';
import { countryLabel, formatCount, formatDay, formatMoney, UNAVAILABLE } from './present.ts';

export type CohortAnchor = 'REGISTRATION' | 'FTD';
export type CohortGranularity = 'MONTHS' | 'DAYS';
export type CohortPeriod = 'MONTH' | 'WEEK';
export type CohortDimension =
  | 'none' | 'affiliate' | 'campaign' | 'brand' | 'product' | 'country'
  | 'sub_id_1' | 'sub_id_2' | 'sub_id_3' | 'sub_id_4' | 'sub_id_5'
  | 'sub_id_6' | 'sub_id_7' | 'sub_id_8' | 'sub_id_9' | 'sub_id_10';

export const COHORT_SUB_IDS: readonly CohortDimension[] = [
  'sub_id_1', 'sub_id_2', 'sub_id_3', 'sub_id_4', 'sub_id_5',
  'sub_id_6', 'sub_id_7', 'sub_id_8', 'sub_id_9', 'sub_id_10',
];

export interface CohortCellWire {
  readonly group_value: string | null;
  readonly group_id: string | null;
  readonly cohort: string;
  readonly checkpoint: string;
  readonly offset: number;
  readonly currency: string;
  /** null, like every count and figure, only on a withheld cell. */
  readonly players: number | null;
  readonly elapsed_players: number | null;
  readonly complete: boolean;
  readonly ftds: number | null;
  readonly depositors: number | null;
  readonly repeat_depositors: number | null;
  readonly deposit_count: number | null;
  readonly active_players: number | null;
  readonly retention_pct: number | null;
  readonly deposits_minor: string | null;
  readonly withdrawals_minor: string | null;
  readonly ggr_minor: string | null;
  readonly bonus_cost_minor: string | null;
  readonly ngr_minor: string | null;
  readonly commission_minor: string | null;
  readonly ngr_per_player_minor: string | null;
  readonly commission_other_currency_lines: number | null;
  readonly unconverted_events: number | null;
  /**
   * An affiliate's small cohorts added together into one "Other (small
   * cohorts)" row, per currency (cohort `OTHER`); `withheld` while that pool
   * is itself under the brand's minimum cohort size, with no figure sent.
   */
  readonly pooled?: boolean;
  readonly withheld?: boolean;
}

/** The cohort key the API gives the pooled "Other (small cohorts)" row. */
export const POOLED_COHORT = 'OTHER';

export interface CohortMatrixWire {
  readonly anchor: CohortAnchor;
  readonly granularity: CohortGranularity;
  readonly cohort_period: CohortPeriod;
  readonly dimension: CohortDimension;
  readonly defaults: { readonly anchor: CohortAnchor; readonly granularity: CohortGranularity };
  readonly checkpoints: readonly { readonly key: string; readonly offset: number }[];
  readonly from: string;
  readonly to: string;
  readonly range_note?: string;
  readonly group_note?: string;
  readonly unconverted_note?: string;
  readonly unavailable_because?: string;
  /** Set when small cohorts were pooled or withheld from this affiliate. */
  readonly small_cohorts_because?: string;
  /** Set for an affiliate whose brand protects small cohorts: no split, no filter. */
  readonly breakdowns_off_because?: string;
  readonly cells: readonly CohortCellWire[];
}

/** What the screen asks for. Absent anchor or checkpoints: the brand's own choice. */
export interface CohortQuery {
  readonly anchor?: CohortAnchor;
  readonly granularity?: CohortGranularity;
  readonly period: CohortPeriod;
  readonly dimension: CohortDimension;
  /** A sub-ID value to narrow to, and which of the ten carries it. */
  readonly sub_id?: string;
  readonly sub_id_index?: string;
}

/**
 * The query string for a cohort request. Always in the original currencies:
 * the report keeps money per currency and never adds currencies together.
 */
export function cohortQueryParams(q: CohortQuery): Record<string, string> {
  return {
    ...(q.anchor ? { anchor: q.anchor } : {}),
    ...(q.granularity ? { granularity: q.granularity } : {}),
    period: q.period,
    dimension: q.dimension,
    ...(q.sub_id ? { sub_id: q.sub_id, sub_id_index: q.sub_id_index ?? '1' } : {}),
    display_currency: DISPLAY_CURRENCY_ORIGINAL,
  };
}

export interface CohortPlayerWire {
  readonly player_ref: string;
  readonly registered_at: string | null;
  readonly first_deposit_at: string | null;
  readonly country: string | null;
  readonly deposit_count: number;
  readonly deposits_minor: string | null;
  readonly withdrawals_minor: string | null;
  readonly ggr_minor: string | null;
  readonly ngr_minor: string | null;
  readonly commission_minor: string | null;
  readonly active: boolean | null;
  readonly currency: string;
}

type Money = 'deposits_minor' | 'withdrawals_minor' | 'ggr_minor' | 'bonus_cost_minor' | 'ngr_minor'
  | 'commission_minor' | 'ngr_per_player_minor';
type Count = 'players' | 'ftds' | 'depositors' | 'repeat_depositors' | 'deposit_count' | 'active_players';

export type CohortMetric =
  | 'retention' | 'players' | 'ftds' | 'depositors' | 'repeat_depositors' | 'deposit_count' | 'active_players'
  | 'deposits' | 'withdrawals' | 'ggr' | 'bonus_cost' | 'ngr' | 'commission' | 'ngr_per_player';

interface MetricDef {
  readonly value: CohortMetric;
  readonly label: KitMessageKey;
  readonly money?: Money;
  readonly count?: Count;
}

export const COHORT_METRICS: readonly MetricDef[] = [
  { value: 'retention', label: 'cohort.metric.retention' },
  { value: 'active_players', label: 'cohort.metric.active', count: 'active_players' },
  { value: 'players', label: 'cohort.metric.players', count: 'players' },
  { value: 'ftds', label: 'cohort.metric.ftds', count: 'ftds' },
  { value: 'depositors', label: 'cohort.metric.depositors', count: 'depositors' },
  { value: 'repeat_depositors', label: 'cohort.metric.repeat', count: 'repeat_depositors' },
  { value: 'deposit_count', label: 'cohort.metric.depositCount', count: 'deposit_count' },
  { value: 'deposits', label: 'cohort.metric.deposits', money: 'deposits_minor' },
  { value: 'withdrawals', label: 'cohort.metric.withdrawals', money: 'withdrawals_minor' },
  { value: 'ggr', label: 'cohort.metric.ggr', money: 'ggr_minor' },
  { value: 'bonus_cost', label: 'cohort.metric.bonus', money: 'bonus_cost_minor' },
  { value: 'ngr', label: 'cohort.metric.ngr', money: 'ngr_minor' },
  { value: 'commission', label: 'cohort.metric.commission', money: 'commission_minor' },
  { value: 'ngr_per_player', label: 'cohort.metric.ngrPerPlayer', money: 'ngr_per_player_minor' },
];

export function isMoneyMetric(metric: CohortMetric): boolean {
  return COHORT_METRICS.some((m) => m.value === metric && m.money !== undefined);
}

export function cohortDimensionLabel(d: CohortDimension): string {
  if (d.startsWith('sub_id_')) return kitText('cohort.dim.subId', { n: d.slice('sub_id_'.length) });
  return kitText(`cohort.dim.${d}` as KitMessageKey);
}

export interface CohortCellView {
  readonly checkpoint: string;
  readonly text: string;
  /** 0 to 1 against the largest value in the grid; null where there is nothing to shade. */
  readonly heat: number | null;
  readonly complete: boolean;
  /** Why the cell looks the way it does, for its tooltip. */
  readonly title: string;
  /** The value the export writes: digits, never a formatted amount. */
  readonly raw: string;
}

export interface CohortRowView {
  readonly key: string;
  readonly cohort: string;
  readonly cohortLabel: string;
  readonly groupLabel: string | null;
  /** `null` is the unset group (the drill sends it empty); undefined is no grouping. */
  readonly groupKey: string | null | undefined;
  /** null on a withheld row: the screen says "Insufficient cohort size". */
  readonly players: number | null;
  /** The pooled "Other (small cohorts)" row: many cohorts, so no drill. */
  readonly pooled: boolean;
  readonly cells: readonly CohortCellView[];
}

export interface CohortGridView {
  readonly checkpoints: readonly string[];
  readonly rows: readonly CohortRowView[];
  /** Currencies the report holds money in; a money metric shows one at a time. */
  readonly currencies: readonly string[];
  readonly currency: string | null;
  readonly grouped: boolean;
  readonly notes: readonly string[];
  readonly commissionOtherLines: number;
}

function cohortLabel(cohort: string, period: CohortPeriod): string {
  return period === 'WEEK' ? kitText('cohort.weekOf', { d: formatDay(cohort) }) : cohort;
}

function groupLabelOf(value: string | null, dimension: CohortDimension): string {
  if (value === null || value === '') return kitText('cohort.unset');
  return dimension === 'country' ? countryLabel(value) : value;
}

/**
 * The grid for one metric. `currency` picks which currency a money metric
 * shows; null (or one the report does not hold) takes the first.
 */
export function cohortGrid(
  data: CohortMatrixWire,
  metric: CohortMetric,
  currency: string | null,
): CohortGridView {
  const def = COHORT_METRICS.find((m) => m.value === metric) ?? COHORT_METRICS[0]!;
  const currencies = [...new Set(data.cells.map((c) => c.currency))].sort();
  const shown = def.money ? (currency && currencies.includes(currency) ? currency : currencies[0] ?? null) : null;
  const grouped = data.dimension !== 'none';
  const checkpoints = data.checkpoints.map((c) => c.key);

  // Rows by cohort and group; cells by checkpoint, with every currency's row.
  // A pooled row is per currency (that is how it was pooled), so it keys on it.
  const byRow = new Map<string, { cohort: string; groupValue: string | null; groupId: string | null;
    pooled: boolean; currency: string; byCheckpoint: Map<string, CohortCellWire[]> }>();
  for (const c of data.cells) {
    const pooled = c.pooled === true;
    const key = JSON.stringify([c.cohort, c.group_id, c.group_value, ...(pooled ? [c.currency] : [])]);
    let row = byRow.get(key);
    if (!row) {
      row = {
        cohort: c.cohort, groupValue: c.group_value, groupId: c.group_id, pooled, currency: c.currency,
        byCheckpoint: new Map(),
      };
      byRow.set(key, row);
    }
    row.byCheckpoint.set(c.checkpoint, [...(row.byCheckpoint.get(c.checkpoint) ?? []), c]);
  }

  type Valued = { value: number | null; text: string; raw: string; complete: boolean; title: string };
  const valueOf = (all: readonly CohortCellWire[]): Valued => {
    // A money cell is one currency's: its completeness is that currency's players'.
    const parts = def.money ? all.filter((p) => p.currency === shown) : all;
    // Under the brand's minimum cohort size: nothing was sent, and it says so.
    if (parts.some((p) => p.withheld)) {
      return { value: null, text: kitText('cohort.withheld'), raw: '', complete: true, title: '' };
    }
    const players = parts.reduce((s, p) => s + (p.players ?? 0), 0);
    const elapsed = parts.reduce((s, p) => s + (p.elapsed_players ?? 0), 0);
    const complete = parts.length > 0 && parts.every((p) => p.complete);
    const title = complete
      ? ''
      : kitText('cohort.incomplete', { n: formatCount(elapsed), total: formatCount(players) });
    if (parts.length === 0) return { value: null, text: '', raw: '', complete: true, title: '' };
    if (metric === 'retention') {
      const active = parts.reduce<number | null>(
        (s, p) => (s === null || p.active_players === null ? null : s + p.active_players), 0);
      if (!complete || active === null || players === 0) return { value: null, text: '—', raw: '', complete, title };
      const pct = Math.round((active / players) * 1000) / 10;
      return { value: pct, text: `${pct}%`, raw: String(pct), complete, title };
    }
    if (def.count) {
      const field = def.count;
      const sum = parts.reduce<number | null>(
        (s, p) => (s === null || p[field] === null ? null : s + (p[field] as number)), 0);
      if (sum === null) return { value: null, text: complete ? UNAVAILABLE : '—', raw: '', complete, title };
      return { value: sum, text: formatCount(sum), raw: String(sum), complete, title };
    }
    const field = def.money!;
    const part = parts.find((p) => p.currency === shown);
    const amount = part ? part[field] : null;
    if (!part) return { value: null, text: '', raw: '', complete, title };
    if (amount === null) return { value: null, text: UNAVAILABLE, raw: '', complete, title };
    return {
      value: Number(amount),
      text: formatMoney({ amount_minor: amount, currency: part.currency }),
      raw: amount,
      complete,
      title,
    };
  };

  const prepared = [...byRow.entries()]
    .sort(([, a], [, b]) => a.cohort.localeCompare(b.cohort)
      || (a.groupValue ?? '').localeCompare(b.groupValue ?? ''))
    .map(([key, r]) => {
      const first = r.byCheckpoint.get(checkpoints[0] ?? '') ?? [...r.byCheckpoint.values()][0] ?? [];
      const counted = def.money ? first.filter((p) => p.currency === shown) : first;
      const players = counted.some((p) => p.withheld)
        ? null
        : counted.reduce((s, p) => s + (p.players ?? 0), 0);
      return {
        key, r, players,
        values: checkpoints.map((k) => ({ k, v: valueOf(r.byCheckpoint.get(k) ?? []) })),
      };
    })
    // A money metric in one currency leaves out the rows with no player in it.
    .filter((x) => !def.money || x.players === null || x.players > 0)
    // A pooled row of another currency has nothing in a money metric's one.
    .filter((x) => !def.money || !x.r.pooled || x.r.currency === shown);

  const magnitudes = prepared.flatMap((x) => x.values.map((v) => Math.abs(v.v.value ?? 0)));
  const max = metric === 'retention' ? 100 : Math.max(0, ...magnitudes);

  const rows: CohortRowView[] = prepared.map(({ key, r, players, values }) => ({
    key,
    cohort: r.cohort,
    cohortLabel: r.pooled
      ? `${kitText('cohort.pooled')}${currencies.length > 1 ? ` · ${r.currency}` : ''}`
      : cohortLabel(r.cohort, data.cohort_period),
    pooled: r.pooled,
    // The pool spans groups, so it names none rather than reading as "(not set)".
    groupLabel: grouped ? (r.pooled ? '' : groupLabelOf(r.groupValue, data.dimension)) : null,
    groupKey: grouped ? r.groupId : undefined,
    players,
    cells: values.map(({ k, v }) => ({
      checkpoint: k,
      text: v.text,
      heat: v.value === null || max === 0 ? null : Math.min(1, Math.abs(v.value) / max),
      complete: v.complete,
      title: v.title,
      raw: v.raw,
    })),
  }));

  const notes = [
    data.unavailable_because, data.range_note, data.group_note, data.unconverted_note,
    data.small_cohorts_because,
  ].filter((n): n is string => typeof n === 'string' && n !== '');

  return {
    checkpoints,
    rows,
    currencies,
    currency: shown,
    grouped,
    notes,
    commissionOtherLines: data.cells
      .filter((c) => c.checkpoint === checkpoints[checkpoints.length - 1])
      .reduce((s, c) => s + (c.commission_other_currency_lines ?? 0), 0),
  };
}

/** The grid as an export: headers, stable keys and the raw values the screen shows. */
export function cohortExport(grid: CohortGridView, metricLabel: string): {
  headers: string[]; keys: string[]; rows: string[][];
} {
  const headers = [
    kitText('cohort.col.cohort'),
    ...(grid.grouped ? [kitText('cohort.col.group')] : []),
    kitText('cohort.col.players'),
    ...grid.checkpoints.map((k) => `${metricLabel} ${k}${grid.currency ? ` (${grid.currency})` : ''}`),
  ];
  const keys = ['cohort', ...(grid.grouped ? ['group'] : []), 'players', ...grid.checkpoints.map((k) => k.toLowerCase())];
  const rows = grid.rows.map((r) => [
    r.cohort,
    ...(grid.grouped ? [r.groupLabel ?? ''] : []),
    r.players === null ? kitText('cohort.withheld') : String(r.players),
    ...r.cells.map((c) => (c.complete ? c.raw : c.raw === '' ? '' : `${c.raw}*`)),
  ]);
  return { headers, keys, rows };
}

export interface CohortPlayerView {
  readonly playerRef: string;
  readonly registered: string;
  readonly firstDeposit: string;
  readonly country: string;
  readonly depositCount: string;
  readonly deposits: string;
  readonly withdrawals: string;
  readonly ggr: string;
  readonly ngr: string;
  readonly commission: string;
  readonly active: string;
}

/** The players behind a cell, formatted. */
export function cohortPlayers(rows: readonly CohortPlayerWire[]): CohortPlayerView[] {
  const money = (v: string | null, c: string) => formatMoney({ amount_minor: v, currency: c });
  return rows.map((r) => ({
    playerRef: r.player_ref,
    registered: r.registered_at ? formatDay(r.registered_at.slice(0, 10)) : '—',
    firstDeposit: r.first_deposit_at ? formatDay(r.first_deposit_at.slice(0, 10)) : '—',
    country: countryLabel(r.country),
    depositCount: formatCount(r.deposit_count),
    deposits: money(r.deposits_minor, r.currency),
    withdrawals: money(r.withdrawals_minor, r.currency),
    ggr: money(r.ggr_minor, r.currency),
    ngr: money(r.ngr_minor, r.currency),
    commission: money(r.commission_minor, r.currency),
    active: r.active === null ? UNAVAILABLE : kitText(r.active ? 'cohort.yes' : 'cohort.no'),
  }));
}
